import {
  BaseError,
  HTTPRequestError,
  HTTPResponseError,
  punctuateSentenceWithPeriod,
  UnknownError,
  type BaseErrorProps,
  type ErrorCtxType,
  type HTTPResponseErrorProps,
} from "@validereinc/utilities";
import type { InstanceOptions } from "modern-errors";
import { isResponseErrorType } from "./schemas.ts";

const DefaultResponseErrorExplanation =
  "An unknown error occurred while interacting with the API.";

export type DomainAdapterErrorProps = {
  /** the adapters or adapter method calls that were involved in the error (e.g. "UserAdapter.createOne" or "ReportAdapter.versions.getOne" or "PermissionsAdapter") */
  adapterCalls: string[] | undefined;
  /** the data that was passed to the adapter calls above, in the same order, if available (e.g. [{ id: "123" }] for ["UserAdapter.getOne"]) */
  adapterCallData: unknown[] | undefined;
};
/**
 * Error class for errors that occur when interacting with a domain adapter.
 *
 * @param message - the error message
 * @param options - options for the error
 * @param options.adapterCalls - the adapters or adapter method calls that were involved in the error
 */
export const DomainAdapterError = HTTPResponseError.subclass(
  "DomainAdapterError",
  {
    custom: class extends HTTPResponseError implements DomainAdapterErrorProps {
      /** @see {@link DomainAdapterErrorProps.adapterCalls} */
      readonly adapterCalls: DomainAdapterErrorProps["adapterCalls"];
      /** @see {@link DomainAdapterErrorProps.adapterCallData} */
      readonly adapterCallData: DomainAdapterErrorProps["adapterCallData"];

      constructor(
        message: string,
        options: InstanceOptions &
          Partial<
            BaseErrorProps & DomainAdapterErrorProps & HTTPResponseErrorProps
          > = {}
      ) {
        const causeName = (() => {
          if (
            options.cause instanceof BaseError ||
            options.cause instanceof Error
          ) {
            return options.cause.name;
          } else {
            return;
          }
        })();
        const causeCopy = (() => {
          if (options.cause instanceof HTTPResponseError) {
            const explanation: string = options.cause.getExplanation(
              options.errorCtx
            );

            return (
              getAPIResponseError(options.cause.responseBodyJson) || explanation
            );
          } else if (options.cause instanceof HTTPRequestError) {
            return options.cause.getExplanation(options.errorCtx);
          } else if (options.cause instanceof Error) {
            return options.cause?.message;
          } else {
            return;
          }
        })();

        super(message, {
          // collapse properties from cause into error in certain cases, just
          // like modern-error does by default. can be overridden by provided options.
          ...(options.cause instanceof HTTPResponseError ||
          options.cause instanceof HTTPRequestError
            ? options.cause.toJSON({
                include: [
                  "code",
                  "severity",
                  "status",
                  "method",
                  "endpoint",
                  "responseBodyJson",
                  "responseContentType",
                  "requestContentType",
                  "requestQuery",
                  "requestBodyJson",
                ],
              })
            : {}),
          ...options,
        });

        if (causeCopy) this._causeExplanation = causeCopy;
        if (causeName) this._causeName = causeName;
        this.adapterCalls = options.adapterCalls;
        this.adapterCallData = options.adapterCallData;
        this._getExplanation = (ctx?: ErrorCtxType): string => {
          const allCtx = this.getMergedErrorContext({
            adapterCalls: options.adapterCalls,
            adapterCallData: options.adapterCallData,
            ...ctx,
          });

          // provided explanation getter takes precedence over API response error
          if (options.getExplanation) {
            return options.getExplanation(allCtx);
          }

          // if there is a cause copy, it will automatically be considered by
          // BaseError.getExplanation, so just return the error msg or default
          // explanation and defer to the cause explanation to provide richer details
          if (causeCopy) {
            return this.message || DefaultResponseErrorExplanation;
          }

          // otherwise, attempt to extract the error message from the API response as the explanation
          const apiResponseError = this.responseError;

          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          return (
            apiResponseError || this.message || DefaultResponseErrorExplanation
          );
        };
      }

      /**
       * Get the error message from the API response.
       *
       * @returns the error message from the API response, if available
       */
      get responseError(): string | undefined {
        return getAPIResponseError(this.responseBodyJson);
      }

      // REVIEW: this is an intentional override of the method from the base class, but typing in modern-errors is not working
      /**
       * @internal
       */
      // @ts-expect-error - there is a typing error in modern-errors
      getExplanationOfAggregateErrors(
        ctx: ErrorCtxType = {},
        opts: { showErrorName?: boolean } = {}
      ): string {
        return (
          this.errors
            ?.map<string | undefined>((error) => {
              if (error instanceof HTTPResponseError) {
                const explanation: string = error.getExplanation(
                  this.getMergedErrorContext(ctx),
                  opts
                );

                return (
                  punctuateSentenceWithPeriod(
                    getAPIResponseError(error.responseBodyJson) || ""
                  ) || explanation
                );
              }

              return UnknownError.normalize(error).getExplanation(
                this.getMergedErrorContext(ctx),
                opts
              );
            })
            .join(" ") ?? ""
        );
      }
    },
  }
);

/**
 * Get the error message from an API response of some shape/type, if known and
 * available.
 *
 * @param data - the API response
 * @returns the error message from the API response, if available
 */
function getAPIResponseError(data: unknown): string | undefined {
  if (isResponseErrorType(data)) {
    return data.error;
  } else {
    return;
  }
}
